import React, { useState, useEffect, useMemo } from 'react'; // Import hooks
import { Box, Typography, Button, Paper, Divider, FormControlLabel, Checkbox } from '@mui/material'; // Simplified imports
import PrintIcon from '@mui/icons-material/Print';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
// We will NOT use the interactive ProgrammeSelector here
// import ProgrammeSelector from '../../components/ProgrammeSelector'; 
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { UnikLog } from '../../assets';
import useCustomToast from '../../hooks/useToast';
import { ProgrammeAPI } from '../../utilities/ProgrammeAPI'; // Import ProgrammeAPI

// New sub-component for rendering a static, printable list of programmes
const PrintableProgrammeList = ({ field, allProgrammes, loading, error }) => {
    // Filter programmes based on the field's configuration
    const filteredProgrammes = useMemo(() => {
        if (!allProgrammes || allProgrammes.length === 0) return [];

        return allProgrammes.filter(programme => {
            const matchesLevel = !field.programmeLevel || field.programmeLevel === 'all' || 
                                 (programme.level && programme.level.toLowerCase() === field.programmeLevel.toLowerCase());
            const matchesFaculty = !field.facultyFilter || field.facultyFilter === 'all' || 
                                  (programme.faculty && programme.faculty.toLowerCase() === field.facultyFilter.toLowerCase());
            return matchesLevel && matchesFaculty;
        });
    }, [allProgrammes, field.programmeLevel, field.facultyFilter]);

    // Group the filtered programmes by faculty and then by department
    const groupedProgrammes = useMemo(() => {
        const grouped = {};
        filteredProgrammes.forEach(programme => {
            const faculty = programme.faculty || 'Other Faculties';
            const department = programme.department || 'Other Departments';

            if (!grouped[faculty]) {
                grouped[faculty] = {};
            }
            if (!grouped[faculty][department]) {
                grouped[faculty][department] = [];
            }
            grouped[faculty][department].push(programme);
        });
        return grouped;
    }, [filteredProgrammes]);

    if (loading) {
        return <Typography sx={{ fontStyle: 'italic', mt: 1 }}>Loading available programmes...</Typography>;
    }

    if (error) {
        return <Typography color="error" sx={{ mt: 1 }}>Failed to load programmes: {error}</Typography>;
    }

    if (filteredProgrammes.length === 0) {
        return <Typography sx={{ fontStyle: 'italic', mt: 1 }}>No programmes available for this selection.</Typography>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            {Object.entries(groupedProgrammes).map(([faculty, departments]) => (
                <Box key={faculty} sx={{ mb: 2, pageBreakInside: 'avoid' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #ccc', pb: 1, mb: 1 }}>
                        {faculty}
                    </Typography>
                    {Object.entries(departments).map(([department, programmes]) => (
                        <Box key={department} sx={{ pl: 2, mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {department}
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                                {programmes.map(programme => (
                                    <FormControlLabel
                                        key={programme.id}
                                        control={<Checkbox disabled sx={{ p: 0.5 }} />} // Disabled checkbox for printing
                                        label={programme.name}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
};


const FormPrint = ({ formData, onClose }) => {
    const form = typeof formData.formConfigJson === 'string' 
        ? JSON.parse(formData.formConfigJson) 
        : formData.formConfigJson;
    const showToast = useCustomToast();

    // State for managing programme data
    const [allProgrammes, setAllProgrammes] = useState([]);
    const [loadingProgrammes, setLoadingProgrammes] = useState(true);
    const [programmesError, setProgrammesError] = useState(null);

    // Fetch all programme data when the component mounts
    useEffect(() => {
        const loadProgrammes = async () => {
            try {
                setLoadingProgrammes(true);
                const response = await ProgrammeAPI.getActiveStructuredProgrammes();
                
                // Flatten the structured data into a single list
                let flattened = [];
                if (response.data) {
                    for (const facultyKey in response.data) {
                        const facultyObj = response.data[facultyKey];
                        for (const departmentKey in facultyObj) {
                            const departmentObj = facultyObj[departmentKey];
                            for (const levelKey in departmentObj) {
                                const levelArray = departmentObj[levelKey];
                                if (Array.isArray(levelArray)) {
                                    flattened = flattened.concat(levelArray);
                                }
                            }
                        }
                    }
                }
                setAllProgrammes(flattened);
            } catch (err) {
                console.error("Failed to load programmes for printing", err);
                setProgrammesError(err.message);
            } finally {
                setLoadingProgrammes(false);
            }
        };

        loadProgrammes();
    }, []);

    const getBase64ImageFromURL = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.height = img.naturalHeight;
                canvas.width = img.naturalWidth;
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    const handlePrint = async () => {
        showToast("Generating PDF File","Please wait until the PDF file is being generated...",'info');
        const printButtonContainer = document.querySelector('.print-button-container');
        if (printButtonContainer) printButtonContainer.style.display = 'none';

        const element = document.getElementById('printable-content');
        const canvas = await html2canvas(element, { scale: 2, useCORS: true });
        
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = canvas.height * imgWidth / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        const totalPages = pdf.internal.getNumberOfPages();
        const logoData = await getBase64ImageFromURL(UnikLog);

        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.text(`Page ${i} of ${totalPages}`, imgWidth - 30, pageHeight - 10);
            pdf.addImage(logoData, 'PNG', 15, pageHeight - 15, 25, 8); // Adjusted logo size/pos
        }
        
        if (printButtonContainer) printButtonContainer.style.display = 'flex';
        pdf.save(`${form.name}.pdf`);
    };

    return (
        <Box sx={{ p: 3 }}>
        {/* Print and close buttons visible only on screen */}
        <Box 
            className="print-button-container"
            sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            '@media print': { display: 'none' } 
            }}
        >
            <Typography variant="subtitle1" gutterBottom>
                Category Type: {form.type} | Created: {new Date(formData.createdAt).toLocaleDateString()}
                <br />
                Name: {form.name}
            </Typography>
            <Box>
            <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ mr: 2 }}
            >
                Generate PDF
            </Button>
            <Button 
                variant="contained"
                color='secondary'
                startIcon={<FormatListBulletedIcon />}
                onClick={onClose}
            >
                List All Forms
            </Button>
            </Box>
        </Box>

        {/* Printable content */}
        <Box 
            id="printable-content"
            sx={{
            '@media screen': { 
                border: '1px solid #ddd', 
                p: 3, 
            },
            '@media print': {
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0
            }
            }}
        >
            {/* Form header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {form.name}
                </Typography>
                <Typography variant="subtitle1">
                    {form.type === 'undergraduate' && 'UNDERGRADUATE APPLICATION FORM'}
                    {form.type === 'graduate' && 'GRADUATE APPLICATION FORM'}
                    {form.type === 'shortcourse' && 'SHORT COURSE APPLICATION FORM'}
                </Typography>
            </Box>

            {/* Form sections */}
            <style>
                {`          
                @page {
                    size: A4;
                    margin: 15mm;
                }
                @media print {
                    body {
                        background: white !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .MuiPaper-root {
                        background-color: transparent !important;
                        box-shadow: none !important;
                        border: 1px solid #eee !important;
                    }
                }
                `}
            </style>
            {form.sections.map((section, sectionIndex) => (
            <Paper 
                key={section.id || sectionIndex} 
                elevation={0} // Use 0 elevation for cleaner print
                sx={{ 
                    p: 3, 
                    mb: 3,
                    border: '1px solid #eee', // Lighter border for screen
                    pageBreakInside: 'avoid',
                }}
            >
                <Typography variant="h5" gutterBottom>{section.name}</Typography>
                <Divider sx={{ mb: 2 }} />
                
                {section.fields.map((field) => {
                switch (field.type) {
                    case 'label':
                        return (
                            <Typography key={field.id} variant="h6" gutterBottom>
                                {field.label}
                            </Typography>
                        );
                    
                    case 'text':
                    case 'number':
                    case 'textarea':
                    case 'email':
                    case 'date':
                        return (
                            <Box key={field.id} sx={{ mt: 2, mb: 1, pageBreakInside: 'avoid' }}>
                                <Typography variant="body1" sx={{ mb: 0.5 }}>{field.label}{field.required ? ' *' : ''}:</Typography>
                                <Box sx={{ borderBottom: '1px dotted black', height: '24px' }} />
                            </Box>
                        );
                    
                    case 'select':
                    case 'radio':
                    case 'checkbox':
                        const options = field.options || (field.type === 'checkbox' ? ['Yes'] : []);
                        return (
                            <Box key={field.id} sx={{ mt: 2, pageBreakInside: 'avoid' }}>
                                <Typography variant="body1">{field.label}{field.required ? ' *' : ''}:</Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                    {options.map((option) => (
                                        <FormControlLabel
                                            key={option}
                                            control={<Checkbox disabled />}
                                            label={option}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        );
                    
                    // --- THIS IS THE IMPORTANT CHANGE ---
                    case 'programme-selector':
                        return (
                            <Box key={field.id} sx={{ mt: 2, pageBreakInside: 'avoid' }}>
                                <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    {field.label}{field.required ? ' *' : ''}:
                                </Typography>
                                <Typography variant="caption" display="block" sx={{mb: 1}}>
                                    {field.maxSelections > 1 
                                        ? `Please select up to ${field.maxSelections} choices.`
                                        : 'Please select one choice.'
                                    }
                                </Typography>
                                <PrintableProgrammeList
                                    field={field}
                                    allProgrammes={allProgrammes}
                                    loading={loadingProgrammes}
                                    error={programmesError}
                                />
                            </Box>
                        );
                    // --- END OF CHANGE ---

                    default:
                        return null;
                }
                })}
            </Paper>
            ))}

            <Box className="print-footer" sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center', fontStyle: 'italic' }}>
                <Typography variant="body2">
                    This is an official form. Please fill it completely and accurately.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Form ID: {formData.id} | Printed on: {new Date().toLocaleDateString()}
                </Typography>
            </Box>
        </Box>
        </Box>
    );
};

export default FormPrint;